import { call, put, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { RegistererState } from 'sip.js';
import {
  setUniqueId,
  setDuplicateState,
} from '../../state/singleInstance/singleInstanceSlice';
import { selectActiveCallCount } from '../../state/callSessions/callSessionsSlice';
import {
  selectRegisterer,
  setRegisterer,
  setUA,
} from '../../state/userAgent/userAgentSlice';
import store from '../../state/store';

const INITIAL_CHANNEL = 'initialBroadcast';
const REFRESH_CHANNEL = 'refresh';
const HANDSHAKE_TIMEOUT_MS = 500;

export function* broadcastChannelSaga() {
  if (typeof BroadcastChannel === 'undefined') return true;

  const uniqueId = uuidv4();
  yield put(setUniqueId(uniqueId));

  const initial = new BroadcastChannel(INITIAL_CHANNEL);

  const result = yield call(() => new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ kind: 'alone' }), HANDSHAKE_TIMEOUT_MS);

    initial.onmessage = (event) => {
      const msg = event.data;
      if (msg?.msg === 'i am on call') {
        clearTimeout(timer);
        resolve({ kind: 'on-call' });
      } else if (msg?.msg === 'you take over') {
        clearTimeout(timer);
        resolve({ kind: 'take-over' });
      }
    };

    initial.postMessage({ msg: 'am i alone?', uniqueId });
  }));

  if (result.kind === 'on-call') {
    initial.close();
    yield put(setDuplicateState({ isDuplicate: true, reason: 'on-call' }));
    yield call(teardownUA);
    return false;
  }

  initial.onmessage = async (event) => {
    const msg = event.data;
    if (msg?.msg !== 'am i alone?') return;

    const activeCount = selectActiveCallCount(store.getState());
    if (activeCount > 0) {
      initial.postMessage({ msg: 'i am on call', uniqueId });
      return;
    }

    const registerer = selectRegisterer(store.getState());
    if (registerer && registerer.state === RegistererState.Registered) {
      try {
        store.dispatch(setRegisterer(null));
        await Promise.resolve();
        registerer.unregister();
        store.dispatch(setUA(null));
      } catch (e) {
        console.error('Error unregistering SIP on step-down:', e);
      }
    }

    initial.postMessage({ msg: 'you take over', uniqueId });
    store.dispatch(setDuplicateState({ isDuplicate: true, reason: 'stepped-down' }));
    initial.close();
    refresh.close();
  };

  const refresh = new BroadcastChannel(REFRESH_CHANNEL);
  refresh.onmessage = (event) => {
    const msg = event.data;
    if (msg?.msg === 'reload check' && msg.uniqueId !== uniqueId) {
      refresh.postMessage({ msg: 'quiet, they are listening', uniqueId });
    }
  };

  return true;
}

function* teardownUA() {
  const registerer = yield select(selectRegisterer);
  if (registerer && registerer.state === RegistererState.Registered) {
    try {
      yield put(setRegisterer(null));
      registerer.unregister();
      yield put(setUA(null));
    } catch (e) {
      console.error('Error unregistering SIP on duplicate detection:', e);
    }
  } else {
    yield put(setRegisterer(null));
    yield put(setUA(null));
  }
}
